import { useEffect, useMemo, useState } from 'react';
import { Grid, Autocomplete, TextField, CircularProgress } from '@mui/material';
import { Address, Municipality } from 'orderme-api-integration-client';
import { useTranslation } from 'react-i18next';
import { LocateClient } from '../../helpers/client';

interface ChooseAddressProps {
  onAddressSelected?: (address: Address | undefined, apartmentNo: string) => void;
}

export const ChooseAddress = ({ onAddressSelected }: ChooseAddressProps) => {
  const [municipalities, setMunicipalities] = useState<Municipality[]>([]);
  const [municipality, setMunicipality] = useState<Municipality>();
  const [address, setAddress] = useState<Address>();
  const [addressOptions, setAddressOptions] = useState<Address[]>([]);
  const [apartmentNo, setApartmentNo] = useState<string>('');
  const [isLoadingMunicipalities, setIsLoadingMunicipalities] = useState<boolean>(false);
  const [isLoadingAddresses, setIsLoadingAddresses] = useState<boolean>(false);
  const locateClient = useMemo(() => new LocateClient(), []);

  const { t } = useTranslation();

  useEffect(() => {
    setIsLoadingMunicipalities(true);
    locateClient
      .getMunicipalities()
      .then((response) => {
        setMunicipalities(response);
      })
      .catch((err) => console.log('error', err))
      .finally(() => setIsLoadingMunicipalities(false));
  }, [locateClient]);

  useEffect(() => {
    if (!municipality) {
      setAddressOptions([]);
    }
    setAddress(null);
  }, [municipality]);

  useEffect(() => {
    if (onAddressSelected) {
      onAddressSelected(address, apartmentNo);
    }
  }, [address, apartmentNo, onAddressSelected]);

  const handleOpen = () => {
    if (address) {
      setAddressOptions([address]);
    } else {
      setAddressOptions([]);
    }
  };

  const handleAddressInputChange = async (lookup: string) => {
    if (!municipality) {
      return;
    }

    const lookupValue = lookup.trim();
    if (lookupValue.length < 4) {
      setAddressOptions([]);
      return;
    }

    try {
      setIsLoadingAddresses(true);
      const results = await locateClient.findAddresses(municipality.code, lookupValue);
      setAddressOptions(results);
    } catch {
      setAddress(null);
      setAddressOptions([]);
    } finally {
      setIsLoadingAddresses(false);
    }
  };

  return (
    <Grid
      container
      spacing={2}
      sx={{
        alignItems: 'center',
        width: '100%',
      }}
    >
      <Grid size={{ xs: 12, md: 'auto' }}>
        <Autocomplete
          disablePortal
          value={municipality ?? null}
          options={municipalities ?? []}
          disabled={isLoadingMunicipalities}
          loading={isLoadingMunicipalities}
          getOptionLabel={(option) => option.name}
          onChange={(_, option) => setMunicipality(option)}
          sx={{
            width: { xs: '100%', md: '300px' },
            '& fieldset': { borderRadius: '15px' },
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('address:addMunicipality')}
              slotProps={{
                input: {
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isLoadingMunicipalities ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : (
                        params.InputProps.endAdornment
                      )}
                    </>
                  ),
                },
              }}
            />
          )}
        />
      </Grid>
      <Grid size="grow">
        <Autocomplete
          disablePortal
          disabled={!municipality}
          value={address ?? null}
          onOpen={handleOpen}
          options={addressOptions}
          loading={isLoadingAddresses}
          getOptionLabel={(o) => o.fullAddress ?? ''}
          onChange={(_, val) => setAddress(val ?? undefined)}
          onInputChange={(_, val, reason) => {
            if (reason !== 'input') {
              return;
            }
            handleAddressInputChange(val);
          }}
          sx={{
            '& fieldset': { borderRadius: '15px' },
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('address:streetNameInput')}
              slotProps={{
                input: {
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isLoadingAddresses ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                },
              }}
            />
          )}
        />
      </Grid>
      <Grid size={12}>
        <TextField
          variant="outlined"
          label={t('address:apartmentNoInput')}
          slotProps={{
            input: {
              sx: {
                borderRadius: '15px',
                width: { xs: '100%', md: '300px' },
              },
            },
          }}
          onChange={(e: any) => setApartmentNo(e.target.value)}
          value={apartmentNo}
        />
      </Grid>
    </Grid>
  );
};
